<template>
  <div class="app-line-tab-wrap">
    <div
      class="line-tab"
      v-for="tab in tabs"
      :key="tab.value"
      :class="{ on: activeTab === tab.value }"
      @click="handleTabChange(tab)"
    >
      {{ t(tab.label) }}
    </div>
  </div>

  <div class="itp-list-con">
    <div class="app-sh-wrap">
      <input
        class="app-t-input app-search-input"
        type="text"
        enterkeyhint="done"
        :placeholder="`${t('enter_search_word')}`"
        :value="searchInput"
        v-cursor-end
        @input="debounceSearch"
        @keydown.enter="handleSearchByEnter"
      />
      <IconSearch />
    </div>

    <div class="scroll-tab-area">
      <div class="app-tab-wrap">
        <button
          type="button"
          class="tab"
          v-for="filter in CHAT_TYPE_FILTERS"
          :key="filter.value"
          :class="{ on: filter.value === activeChatType }"
          @click="handleFilterClick(filter)"
        >
          {{ t(filter.label) }}
        </button>
      </div>
    </div>

    <div class="list-area">
      <p class="total-num">
        {{ t('label_total') }}
        <span>{{ chatList.length }}</span>
        {{ t('label_count_items_gun') }}
      </p>
      <InterpertationUsed :chat-list="chatList" @toggle-favorite="toggleFavorite" @refresh="fetchUserChatList" />

      <div class="view-btn-wrap" v-if="pagination.hasMore">
        <button class="all-view-btn" @click="showMore">
          <span>{{ t('more') }}</span>
          <IconArrow class="icon" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { debounce } from 'lodash';

import InterpertationUsed from '@/components/domains/mobile/InterpertationUsed.vue';
import IconArrow from '@/components/jh-app/icons/IconArrow.vue';
import IconSearch from '@/components/jh-app/icons/IconSearch.vue';
import { useChatList } from '@/composables/domains/mobile/useChatList';

const route = useRoute();
const { t } = useI18n();

const { chatList, fetchUserChatList, toggleFavorite, CHAT_TYPE_FILTERS, setFilter, pagination } = useChatList();

const activeTab = ref<string>('ALL');
const activeChatType = ref<string>('ALL');
const searchInput = ref<string>('');

const tabs = [
  { value: 'ALL', label: 'all_interpretation_list' },
  { value: 'FAVORITE', label: 'favorite_interpretation_list' },
];

const handleTabChange = (tab) => {
  activeTab.value = tab.value;
  activeChatType.value = 'ALL';
  searchInput.value = '';

  setFilter({ isFavorite: tab.value === 'FAVORITE' ? true : null, keyword: '', chatTypes: [] });
};

const handleFilterClick = (filter) => {
  activeChatType.value = filter.value;

  setFilter({ chatTypes: filter.value === 'ALL' ? null : [filter.value] });
};

const handleSearch = () => {
  debounceSearch.cancel();

  setFilter({ keyword: searchInput.value.trim() });
};

const handleSearchByEnter = (e: KeyboardEvent) => {
  handleSearch();
  (e.target as HTMLInputElement).blur();
};

const debounceSearch = debounce(handleSearch, 500);

const showMore = () => {
  fetchUserChatList(pagination.value.nextOffset);
};

onMounted(() => {
  const tab = route.query.tab as string;
  if (tab) {
    activeTab.value = tab.toUpperCase();
    setFilter({ isFavorite: activeTab.value === 'FAVORITE' ? true : null });
  }

  fetchUserChatList();
});
</script>

<style scoped lang="scss">
.itp-list-con {
  padding-top: 2.5rem;
  .app-tab-wrap {
    margin-top: $size-m;
  }

  .list-area {
    margin-top: 1.5rem;
    .total-num {
      font-size: $font-size-psmall;

      span {
        font-weight: 700;
      }
    }
  }
}
.app-sh-wrap {
  position: relative;
  margin-bottom: $size-m;
  .app-search-input {
    background: $gray10;
    padding-left: 3rem;
    border: 1px solid transparent;
  }
  svg {
    position: absolute;
    top: 50%;
    left: 1rem;
    transform: translateY(-50%);
  }
}

.view-btn-wrap {
  display: flex;
  justify-content: center;
  padding: 0.5rem 0;
  .all-view-btn {
    padding: 0.75rem 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    text-decoration: none;
    span {
      font-size: $font-size-psmall;
      color: $color-gray-500;
      font-weight: 500;
    }
    svg {
      transform: rotate(180deg);
    }
  }
}
</style>
